// src/pages/SobeScriptAnalytics.jsx
import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Calendar, Copy, ChevronDown, ChevronUp, X as CloseIcon } from "lucide-react";
import {
  format, subDays, startOfMonth, endOfMonth,
  startOfWeek, endOfWeek,
} from "date-fns";
import { ptBR } from "date-fns/locale";
import { DateRange } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { toast } from "react-toastify";

const inputCls = "w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-transparent text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 outline-none";

function getAuthHeaders() {
  const token = localStorage.getItem("sobevenda_token");
  return { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

/* ── Timezone helpers ──────────────────────────────────────────────────────── */

const DEFAULT_TZ_OFFSET = -3;
function getDateWithTZ(date) {
  const utc = date.getTime() + date.getTimezoneOffset() * 60000;
  return new Date(utc + 3600000 * DEFAULT_TZ_OFFSET);
}
function setStartOfDay(date) { const d = new Date(date); d.setHours(0, 0, 0, 0); return d; }
function setEndOfDay(date)   { const d = new Date(date); d.setHours(23, 59, 59, 999); return d; }

/* ── API helpers ───────────────────────────────────────────────────────────── */

async function fetchVisitsAggregate(script_uuid, dateRange, quickRange) {
  const start = quickRange === "all" ? 0 : setStartOfDay(dateRange[0].startDate).getTime();
  const end   = quickRange === "all" ? Date.now() : setEndOfDay(dateRange[0].endDate).getTime();
  const res = await fetch(`https://api.sobevenda.com.br/api-proxy/script/visits-aggregate-by-ip?script_uuid=${script_uuid}&start_date=${start}&end_date=${end}`, { method: "GET", headers: getAuthHeaders() });
  return await res.json();
}

async function fetchVisitsByIp(script_uuid, ip) {
  const res = await fetch(`https://api.sobevenda.com.br/api-proxy/script/visits-by-ip?script_uuid=${script_uuid}&ip=${ip}`, { method: "GET", headers: getAuthHeaders() });
  return await res.json();
}

async function fetchScriptInfo(uuid) {
  const res = await fetch(`https://api.sobevenda.com.br/api-proxy/script/get-script-by-uuid?uuid=${uuid}`, { method: "GET", headers: getAuthHeaders() });
  if (!res.ok) return null;
  return await res.json();
}

async function fetchStageStats(script_uuid, dateRange, quickRange) {
  const start = quickRange === "all" ? 0 : setStartOfDay(dateRange[0].startDate).getTime();
  const end   = quickRange === "all" ? Date.now() : setEndOfDay(dateRange[0].endDate).getTime();
  const res = await fetch(`https://api.sobevenda.com.br/api-proxy/script/visits-aggregate-by-stage?script_uuid=${script_uuid}&start_date=${start}&end_date=${end}`, { method: "GET", headers: getAuthHeaders() });
  if (!res.ok) return null;
  return await res.json();
}

async function fetchVisitByUuid(visit_uuid) {
  const res = await fetch(`https://api.sobevenda.com.br/api-proxy/script/find-visit-by-uuid?visit_uuid=${encodeURIComponent(visit_uuid)}`, { method: "GET", headers: getAuthHeaders() });
  if (!res.ok) return null;
  return await res.json();
}

async function fetchBlockedIps() {
  const res = await fetch(`https://api.sobevenda.com.br/api-proxy/script/blocked-ips`, { method: "GET", headers: getAuthHeaders() });
  if (!res.ok) return [];
  const data = await res.json();
  return Array.isArray(data) ? data : [];
}

/* ── Field labels ──────────────────────────────────────────────────────────── */

function translateField(key) {
  const map = { gclid:"GCLID", gbraid:"GBRAID", wbraid:"WBRAID", campaignid:"Campanha (ID)", adgroupid:"AdGroup (ID)", creative:"Criativo", keyword:"Palavra-chave", matchtype:"Tipo de correspondência", network:"Rede", device:"Dispositivo do anúncio", placement:"Placement", targetid:"TargetID", feeditemid:"Feed Item", loc_physical_ms:"Local físico", loc_interest_ms:"Local interesse", random:"Random", adposition:"Ad position", extensionid:"ExtensionID", param1:"Parâmetro 1", param2:"Parâmetro 2", param3:"Parâmetro 3", click_id:"Click ID", visit_uuid:"ID da Visita (SVC)", referrer:"Origem", device_type:"Tipo dispositivo", utm_source:"Fonte", utm_medium:"Meio", utm_campaign:"Campanha", utm_term:"Palavra-chave", utm_content:"Conteúdo", city:"Cidade", state:"Estado", country:"País", user_agent:"Navegador/Dispositivo", conversion_name:"Nome da conversão", reached_checkout:"Chegou ao checkout", purchased:"Compra realizada", trafego_pago:"Tráfego pago", url:"URL de destino", ip_classification:"Classificação" };
  return map[key] || key;
}

const PARAM_KEYS = ["gclid","gbraid","wbraid","campaignid","adgroupid","creative","keyword","matchtype","network","device","placement","targetid","feeditemid","loc_physical_ms","loc_interest_ms","random","adposition","extensionid","param1","param2","param3"];
const TRACKING_KEYS = ["click_id","visit_uuid","referrer","device_type"];
const ALL_PARAM_KEYS = [...PARAM_KEYS, ...TRACKING_KEYS];

/* ── Preset button ─────────────────────────────────────────────────────────── */

function PresetBtn({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`h-9 px-3 rounded-lg border text-sm font-medium transition ${
        active
          ? "bg-slate-900 text-white border-slate-900 dark:bg-white dark:text-slate-900 dark:border-white"
          : "border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800"
      }`}
    >
      {children}
    </button>
  );
}

/* ── Stat Card ─────────────────────────────────────────────────────────────── */

function StatCard({ label, hint, value, accent }) {
  return (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4">
      <div className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">{label}</div>
      <div className={`mt-1 text-2xl font-bold ${accent ? "text-emerald-600 dark:text-emerald-400" : "text-slate-900 dark:text-slate-100"}`}>{value}</div>
      {hint && <div className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">{hint}</div>}
    </div>
  );
}

/* ── Funil Stage Card ──────────────────────────────────────────────────────── */

function StageCard({ title, subtitle, cols }) {
  return (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 min-h-[160px] flex flex-col">
      <div className="min-h-[42px]">
        <div className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">{title}</div>
        <div className="text-[11px] text-slate-500 dark:text-slate-400 leading-4 mt-1">{subtitle}</div>
      </div>
      <div className="grid grid-cols-3 gap-3 text-xs items-start mt-auto pt-3">
        {cols.map(({ label, value, accent }) => (
          <div key={label} className="flex flex-col">
            <div className="min-h-[30px] text-[11px] leading-4 text-slate-500 dark:text-slate-400">{label}</div>
            <div className={`mt-1 min-h-[24px] text-xl font-semibold leading-none ${accent ? "text-emerald-600 dark:text-emerald-400" : "text-slate-900 dark:text-slate-100"}`}>{value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Main component ────────────────────────────────────────────────────────── */

export default function SobeScriptAnalytics() {
  const { uuid } = useParams();
  const [searchParams] = useSearchParams();
  const qpVisitUuid = searchParams.get("visit_uuid") || "";
  const qpIp        = searchParams.get("ip") || "";
  const [focusVisitUuid, setFocusVisitUuid] = useState("");

  const [quickRange, setQuickRange] = useState("today");
  const [showCalendar, setShowCalendar] = useState(false);
  const [dateRange, setDateRange] = useState([{ startDate: setStartOfDay(getDateWithTZ(new Date())), endDate: setEndOfDay(getDateWithTZ(new Date())), key: "selection" }]);

  const [scriptInfo, setScriptInfo] = useState(null);
  const [visitsAgregado, setVisitsAgregado] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stageStats, setStageStats] = useState(null);

  const [selectedIp, setSelectedIp] = useState(null);
  const [modalVisits, setModalVisits] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);
  const [expandedIdx, setExpandedIdx] = useState(null);

  const [blockModalOpen, setBlockModalOpen] = useState(false);
  const [ipToBlock, setIpToBlock] = useState("");
  const [blockScope, setBlockScope] = useState("script");
  const [blockNote, setBlockNote] = useState("");
  const [blockingIp, setBlockingIp] = useState(false);
  const [blockedIps, setBlockedIps] = useState([]);
  const [blockedIpsLoading, setBlockedIpsLoading] = useState(false);

  const isFunnel = scriptInfo && (scriptInfo.is_funnel === 1 || scriptInfo.is_funnel === "1" || scriptInfo.is_funnel === true || scriptInfo.funnel_mode === "bridge_vsl");

  const bridge   = stageStats?.bridge   || {};
  const vsl      = stageStats?.vsl      || {};
  const checkout = stageStats?.checkout || {};
  const sales    = stageStats?.sales    || {};

  const bridgePaidUniqueIps    = Number(bridge.paid_unique_ips || 0);
  const bridgePaidUniqueToVsl  = Number(bridge.paid_unique_ips_to_next_stage || 0);
  const bridgeCtr              = Number(bridge.paid_unique_ctr || 0);
  const vslPaidUniqueIps       = Number(vsl.paid_unique_ips || 0);
  const vslPaidUniqueToCheckout = Number(vsl.paid_unique_ips_to_next_stage || 0);
  const vslCtr                 = Number(vsl.paid_unique_ctr || 0);
  const checkoutPaidUniqueIps  = Number(checkout.paid_unique_ips || 0);
  const checkoutToSales        = Number(checkout.paid_unique_ips_to_next_stage || 0);
  const checkoutCtr            = Number(checkout.paid_unique_ctr || 0);
  const paidSales              = Number(sales.paid_sales || 0);

  /* ── Effects ─────────────────────────────────────────────────────────────── */

  useEffect(() => {
    fetchScriptInfo(uuid).then((data) => {
      if (data?.found_script) setScriptInfo(data.found_script);
      else if (data) setScriptInfo(data);
      else setScriptInfo(null);
    });
  }, [uuid]);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchVisitsAggregate(uuid, dateRange, quickRange), fetchStageStats(uuid, dateRange, quickRange)])
      .then(([visitsData, stageData]) => {
        setVisitsAgregado(Array.isArray(visitsData) ? [...visitsData].sort((a, b) => b.data_ultima_visita - a.data_ultima_visita) : []);
        setStageStats(stageData);
      })
      .finally(() => setLoading(false));
  }, [uuid, dateRange, quickRange]);

  useEffect(() => {
    (async () => {
      if (!qpVisitUuid && !qpIp) return;
      if (qpIp) { if (qpVisitUuid) setFocusVisitUuid(qpVisitUuid); await openModal(qpIp); return; }
      if (qpVisitUuid) {
        const v = await fetchVisitByUuid(qpVisitUuid);
        if (v?.ip) { setFocusVisitUuid(qpVisitUuid); await openModal(v.ip); }
        else toast.error("Não consegui localizar o IP dessa visita.");
      }
    })();
  }, [uuid]); // eslint-disable-line

  useEffect(() => {
    if (!focusVisitUuid || !modalVisits?.length) return;
    const target = focusVisitUuid.toLowerCase();
    const idx = modalVisits.findIndex((v) => String(v.visit_uuid || "").toLowerCase() === target);
    if (idx >= 0) {
      setExpandedIdx(idx);
      setTimeout(() => { document.getElementById(`visit-row-${focusVisitUuid}`)?.scrollIntoView({ behavior: "smooth", block: "center" }); }, 80);
    } else { toast.warn("Encontrei o IP, mas não achei essa visita no histórico."); }
  }, [modalVisits, focusVisitUuid]);

  useEffect(() => {
    setBlockedIpsLoading(true);
    fetchBlockedIps().then((data) => setBlockedIps(Array.isArray(data) ? data : [])).finally(() => setBlockedIpsLoading(false));
  }, []);

  /* ── Handlers ────────────────────────────────────────────────────────────── */

  function handlePresetRange(type) {
    if (type === "all") { setQuickRange("all"); setShowCalendar(false); return; }
    const today = getDateWithTZ(new Date());
    let start, end;
    switch (type) {
      case "today":      start = setStartOfDay(today); end = setEndOfDay(today); break;
      case "yesterday":  start = setStartOfDay(subDays(today,1)); end = setEndOfDay(subDays(today,1)); break;
      case "week":       start = setStartOfDay(startOfWeek(today,{weekStartsOn:1})); end = setEndOfDay(endOfWeek(today,{weekStartsOn:1})); break;
      case "last7":      start = setStartOfDay(subDays(today,6)); end = setEndOfDay(today); break;
      case "month":      start = setStartOfDay(startOfMonth(today)); end = setEndOfDay(endOfMonth(today)); break;
      case "lastmonth":  { const ref = subDays(startOfMonth(today),1); start = setStartOfDay(startOfMonth(ref)); end = setEndOfDay(endOfMonth(ref)); break; }
      default:           start = setStartOfDay(today); end = setEndOfDay(today);
    }
    setDateRange([{ startDate: start, endDate: end, key: "selection" }]);
    setQuickRange(type);
    setShowCalendar(false);
  }

  const openModal = async (ip) => {
    setSelectedIp(ip); setModalVisits([]); setExpandedIdx(null); setModalLoading(true);
    const data = await fetchVisitsByIp(uuid, ip);
    setModalVisits(Array.isArray(data) ? [...data].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)) : []);
    setModalLoading(false);
  };

  function openBlockModal(ip) { setIpToBlock(ip || ""); setBlockScope("script"); setBlockNote(""); setBlockModalOpen(true); }

  async function handleConfirmBlockIp() {
    if (!ipToBlock) { toast.error("IP inválido para bloqueio."); return; }
    try {
      setBlockingIp(true);
      const payload = { ip: ipToBlock, scope: blockScope, note: blockNote || "", ...(blockScope === "script" ? { script_uuid: uuid } : {}) };
      const resp = await fetch("https://api.sobevenda.com.br/api-proxy/script/blocked-ips", { method: "POST", headers: getAuthHeaders(), body: JSON.stringify(payload) });
      const data = await resp.json().catch(() => null);
      if (!resp.ok || data?.payload || data?.error || data?.message === "already_blocked") throw new Error(data?.message || data?.payload || data?.error || "Não foi possível bloquear o IP.");
      toast.success(blockScope === "global" ? "IP bloqueado globalmente com sucesso." : "IP bloqueado para este script com sucesso.");
      const refreshed = await fetchBlockedIps();
      setBlockedIps(Array.isArray(refreshed) ? refreshed : []);
      setBlockModalOpen(false); setIpToBlock(""); setBlockNote(""); setBlockScope("script");
    } catch (err) { toast.error(err.message || "Erro ao bloquear IP."); }
    finally { setBlockingIp(false); }
  }

  const handleCopy = (text) => { if (!text) return; navigator.clipboard.writeText(text); toast.success("Copiado!"); };

  function formatIpClass(row) {
    if (row.ip_classification && row.ip_classification !== "none") return row.ip_classification;
    if (row.is_bot) {
      switch (row.bot_type) {
        case "google_bot": return "Bot Google";
        case "google_ads_template": return "Template Google Ads";
        case "social_bot": return "Bot de Rede Social";
        case "generic_bot": return "Bot desconhecido";
        default: return "Bot";
      }
    }
    return "Usuário comum";
  }

  function getBlockedStatus(ip) {
    const clean = String(ip || "").trim();
    if (!clean) return null;
    const global = blockedIps.find((item) => String(item.ip || "").trim() === clean && String(item.scope || "").trim().toLowerCase() === "global");
    if (global) return { isBlocked: true, scope: "global", label: "IP bloqueado global" };
    const script = blockedIps.find((item) => String(item.ip || "").trim() === clean && String(item.scope || "").trim().toLowerCase() === "script" && String(item.script_uuid || "").trim() === String(uuid || "").trim());
    if (script) return { isBlocked: true, scope: "script", label: "IP bloqueado neste script" };
    return { isBlocked: false, scope: null, label: "Bloquear IP" };
  }

  const paidRows = Array.isArray(visitsAgregado) ? visitsAgregado.filter((v) => (v.trafego_pago || 0) > 0) : [];
  const totalPaidVisits    = paidRows.length;
  const totalPaidClicks    = paidRows.filter((v) => Number(v.total_cliques || 0) > 0).length;
  const totalPaidCheckouts = paidRows.filter((v) => Number(v.total_checkouts || 0) > 0).length;
  const totalPaidPurchases = paidRows.filter((v) => Number(v.total_compras || 0) > 0).length;
  const step1Rate = totalPaidVisits    > 0 ? (totalPaidClicks    / totalPaidVisits)    * 100 : 0;
  const step2Rate = totalPaidClicks    > 0 ? (totalPaidCheckouts / totalPaidClicks)    * 100 : 0;
  const step3Rate = totalPaidCheckouts > 0 ? (totalPaidPurchases / totalPaidCheckouts) * 100 : 0;
  const fmtPct = (v) => `${v.toFixed(1)}%`;
  const fmtInt = (n) => Number(n || 0).toLocaleString("pt-BR");

  /* ── Render ──────────────────────────────────────────────────────────────── */

  return (
    <div className="p-4 md:p-6">
      <style>{`
        .rdrCalendarWrapper,.rdrDateRangeWrapper,.rdrMonths,.rdrMonth,.rdrMonthAndYearWrapper,.rdrWeekDays,.rdrDays,.rdrDefinedRangesWrapper,.rdrStaticRange,.rdrInputRanges,.rdrDateDisplayWrapper{background:white!important}
        :root.dark .rdrCalendarWrapper,:root.dark .rdrDateRangeWrapper,:root.dark .rdrMonths,:root.dark .rdrMonth,:root.dark .rdrMonthAndYearWrapper,:root.dark .rdrWeekDays,:root.dark .rdrDays,:root.dark .rdrDefinedRangesWrapper,:root.dark .rdrStaticRange,:root.dark .rdrInputRanges,:root.dark .rdrDateDisplayWrapper{background:#0b1220!important}
        .rdrDateDisplayWrapper,.rdrDefinedRangesWrapper{border:1px solid rgba(148,163,184,.25)!important}
        .rdrWeekDay,.rdrDayNumber span{color:inherit!important}
        .rdrDayDisabled{opacity:.35!important}
        .rdrDayToday .rdrDayNumber span:after{background:currentColor!important}
        .rdrDayHovered,.rdrDayStartPreview,.rdrDayEndPreview,.rdrInRange,.rdrStartEdge,.rdrEndEdge,.rdrSelected{background:rgba(15,23,42,.12)!important;color:inherit!important}
        .rdrMonthAndYearPickers select,.rdrInputRange input{color:inherit!important;background:transparent!important;border:1px solid rgba(148,163,184,.25)!important}
      `}</style>

      <div className="space-y-6">

        {/* Header */}
        <div>
          <div className="text-xs text-slate-500 dark:text-slate-400 mb-0.5">Nome do SobeScript</div>
          <div className="text-base font-bold font-mono break-all text-slate-900 dark:text-slate-100">
            {scriptInfo ? scriptInfo.project_name || scriptInfo.name || "—" : "—"}
          </div>
          <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            Plataforma: {scriptInfo ? scriptInfo.platform || scriptInfo.plataforma || "—" : "—"}
          </div>
        </div>

        {/* Filtros */}
        <div className="flex flex-wrap items-center gap-2">
          {[
            { label: "Hoje",          key: "today"     },
            { label: "Ontem",         key: "yesterday" },
            { label: "Últimos 7 dias",key: "last7"     },
            { label: "Este mês",      key: "month"     },
            { label: "Mês passado",   key: "lastmonth" },
            { label: "Todo período",  key: "all"       },
          ].map((p) => (
            <PresetBtn key={p.key} active={quickRange === p.key} onClick={() => handlePresetRange(p.key)}>
              {p.label}
            </PresetBtn>
          ))}

          <div className="relative">
            <PresetBtn active={quickRange === "custom"} onClick={() => { setShowCalendar((v) => !v); setQuickRange("custom"); }}>
              <Calendar className="inline w-4 h-4 -mt-0.5 mr-1" /> Customizado
            </PresetBtn>
            {showCalendar && quickRange === "custom" && (
              <div className="absolute left-0 top-[44px] z-[9999] rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-2 shadow-xl">
                <DateRange editableDateInputs onChange={(item) => setDateRange([item.selection])} moveRangeOnFirstSelection={false} ranges={dateRange} maxDate={new Date()} locale={ptBR} />
                <div className="flex justify-end gap-2 px-1 pb-1">
                  <button className="h-8 px-3 rounded-lg text-sm border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800" onClick={() => setShowCalendar(false)}>Cancelar</button>
                  <button className="h-8 px-3 rounded-lg text-sm bg-slate-900 text-white hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100" onClick={() => setShowCalendar(false)}>Aplicar</button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Cards funil Bridge+VSL */}
        {isFunnel && stageStats && (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
            <StageCard title="Bridge Page" subtitle="Primeira página do funil (tráfego pago)" cols={[
              { label: "Acessos únicos pagos", value: fmtInt(bridgePaidUniqueIps)   },
              { label: "Sessões na VSL",        value: fmtInt(bridgePaidUniqueToVsl) },
              { label: "CTR BP → VSL",          value: fmtPct(bridgeCtr),            accent: true },
            ]} />
            <StageCard title="VSL" subtitle="Página da carta de vendas (tráfego pago)" cols={[
              { label: "Sessões únicas na VSL",  value: fmtInt(vslPaidUniqueIps)        },
              { label: "Sessões no checkout",    value: fmtInt(vslPaidUniqueToCheckout) },
              { label: "CTR VSL → Checkout",    value: fmtPct(vslCtr),                 accent: true },
            ]} />
            <StageCard title="Checkout (pago)" subtitle="Sessões pagas que chegaram ao checkout" cols={[
              { label: "Checkouts únicos",       value: fmtInt(checkoutPaidUniqueIps) },
              { label: "Sessões que compraram",  value: fmtInt(checkoutToSales)       },
              { label: "Checkout → Venda",      value: fmtPct(checkoutCtr),          accent: true },
            ]} />
            <StageCard title="Vendas (pago)" subtitle="Compras confirmadas vindas de tráfego pago" cols={[
              { label: "Vendas",   value: fmtInt(paidSales)       },
              { label: "Origem",   value: "Tráfego pago"           },
              { label: "Base",     value: "Sessão única"           },
            ]} />
          </div>
        )}

        {/* Cards single page */}
        {!isFunnel && !loading && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <StatCard label="Acessos únicos pagos"    value={fmtInt(totalPaidVisits)}    hint="IPs únicos com origem paga no período" />
              <StatCard label="Cliques únicos na presell" value={fmtInt(totalPaidClicks)}  hint="IPs pagos que clicaram pelo menos 1 vez" />
              <StatCard label="Checkouts únicos"         value={fmtInt(totalPaidCheckouts)} hint="IPs pagos que chegaram ao checkout" />
              <StatCard label="Vendas únicas"            value={fmtInt(totalPaidPurchases)} hint="IPs pagos com compra confirmada" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <StatCard label="Acesso pago → Clique" value={fmtPct(step1Rate)} hint="CTR real por IP único pago"                   accent />
              <StatCard label="Clique → Checkout"    value={fmtPct(step2Rate)} hint="Taxa de IPs que clicaram e chegaram ao checkout" accent />
              <StatCard label="Checkout → Venda"     value={fmtPct(step3Rate)} hint="Conversão de checkout para venda por IP único"  accent />
            </div>
          </>
        )}

        {/* Tabela principal */}
        {loading ? (
          <p className="text-sm text-slate-500 dark:text-slate-400">Carregando…</p>
        ) : visitsAgregado.length === 0 ? (
          <p className="text-sm text-slate-500 dark:text-slate-400">Nenhum acesso encontrado nesse período. Ajuste a data.</p>
        ) : (
          <div className="overflow-auto rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
            <table className="min-w-full text-sm">
              <thead className="sticky top-0 z-10 bg-slate-50 dark:bg-slate-800/40 border-b border-slate-200 dark:border-slate-700">
                <tr>
                  {["IP","Classificação","Cidade","Estado","País","Acessos","Cliques","Checkout","Comprou","Dispositivo","Origem","Última visita","Análise"].map((h) => (
                    <th key={h} className="py-3 px-4 text-center text-xs font-semibold text-slate-600 dark:text-slate-300">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {visitsAgregado.map((v, i) => {
                  const blockedStatus = getBlockedStatus(v.visits_ip);
                  return (
                    <tr key={v.visits_ip + v.visits_script_uuid}
                      className={[
                        "transition-colors",
                        v.total_compras > 0
                          ? "bg-emerald-50 dark:bg-emerald-900/20 ring-1 ring-emerald-200 dark:ring-emerald-800"
                          : i % 2 === 0 ? "bg-white dark:bg-slate-900" : "bg-slate-50/50 dark:bg-slate-800/20",
                        v.total_compras > 0 ? "hover:bg-emerald-100 dark:hover:bg-emerald-900/30" : "hover:bg-slate-50 dark:hover:bg-slate-800/50",
                      ].join(" ")}
                    >
                      <td className="px-4 py-3 text-xs font-mono text-slate-700 dark:text-slate-200 whitespace-nowrap">{v.visits_ip}</td>
                      <td className="px-4 py-2 text-center text-xs text-slate-600 dark:text-slate-300">{formatIpClass(v)}</td>
                      <td className="px-4 py-2 text-center text-xs">{Array.isArray(v.cidade) ? v.cidade[0] : v.cidade || "—"}</td>
                      <td className="px-4 py-2 text-center text-xs">{Array.isArray(v.estado) ? v.estado[0] : v.estado || "—"}</td>
                      <td className="px-4 py-2 text-center text-xs">{Array.isArray(v.pais)   ? v.pais[0]   : v.pais   || "—"}</td>
                      <td className="px-4 py-2 text-center text-xs">{v.total_acessos}</td>
                      <td className="px-4 py-2 text-center text-xs">{v.total_cliques}</td>
                      <td className="px-4 py-2 text-center text-xs">{v.total_checkouts}</td>
                      <td className="px-4 py-2 text-center text-xs">{v.total_compras}</td>
                      <td className="px-4 py-2 text-center text-xs">{Array.isArray(v.dispositivo) ? v.dispositivo[0] : v.dispositivo || "—"}</td>
                      <td className="px-4 py-2 text-center">
                        {v.trafego_pago > 0 ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">Pago</span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400">Orgânico</span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-center text-xs text-slate-600 dark:text-slate-300">{v.data_ultima_visita ? format(new Date(v.data_ultima_visita), "dd/MM/yyyy HH:mm") : "—"}</td>
                      <td className="px-4 py-2 text-center">
                        <div className="flex flex-col items-center gap-1">
                          <button onClick={() => openModal(v.visits_ip)} className="text-xs text-slate-700 dark:text-slate-200 hover:underline font-medium">
                            Análise do Clique
                          </button>
                          {blockedStatus?.isBlocked ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400">
                              {blockedStatus.scope === "global" ? "Bloqueado (global)" : "Bloqueado"}
                            </span>
                          ) : (
                            <button onClick={() => openBlockModal(v.visits_ip)} disabled={blockedIpsLoading} className="text-xs text-red-500 hover:underline disabled:opacity-50">
                              Bloquear IP
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── MODAL VISITAS DO IP ── */}
      {selectedIp && (
        <div className="fixed inset-0 z-[9998] bg-black/60 backdrop-blur-[1px] flex items-start justify-center overflow-y-auto">
          <div className="relative w-full max-w-[1100px] mt-10 mx-4 rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 shadow-2xl">

            <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-slate-200 dark:border-slate-700">
              <h2 className="text-base font-bold">
                Histórico do IP: <span className="font-mono text-sm text-slate-600 dark:text-slate-300 break-all">{selectedIp}</span>
              </h2>
              <button className="p-2 rounded-lg text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800" onClick={() => setSelectedIp(null)}>
                <CloseIcon className="w-4 h-4" />
              </button>
            </div>

            <div className="p-6">
              {modalLoading ? (
                <p className="text-sm text-slate-500">Carregando detalhes…</p>
              ) : (
                <>
                  {/* Resumo */}
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    {[
                      { label: "Total de acessos",  value: modalVisits.length },
                      { label: "Acessos pagos",     value: modalVisits.filter((v) => v.trafego_pago).length },
                      { label: "Cliques em links",  value: modalVisits.filter((v) => v.click_on_presell).length },
                    ].map(({ label, value }) => (
                      <div key={label} className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 p-3 text-center">
                        <div className="text-2xl font-bold text-slate-900 dark:text-slate-100">{value}</div>
                        <div className="text-xs text-slate-500 mt-0.5">{label}</div>
                      </div>
                    ))}
                  </div>

                  {/* Tabela interna */}
                  <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700 max-h-[60vh]">
                    <table className="min-w-full text-xs">
                      <thead className="bg-slate-50 dark:bg-slate-800/40 border-b border-slate-200 dark:border-slate-700">
                        <tr>
                          {["Data","Cidade","Estado","País","Dispositivo","Checkout","Compra","Bot","VPN","Proxy","Cliques","Expandir"].map((h) => (
                            <th key={h} className="px-2 py-2 text-center text-slate-600 dark:text-slate-300 font-semibold">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                        {modalVisits.map((v, i) => (
                          <React.Fragment key={v.visit_uuid || i}>
                            <tr
                              id={`visit-row-${v.visit_uuid}`}
                              className={`cursor-pointer transition ${
                                i % 2 === 0 ? "bg-white dark:bg-slate-900" : "bg-slate-50/50 dark:bg-slate-800/20"
                              } ${v.purchased ? "bg-emerald-50 dark:bg-emerald-900/20" : ""} ${
                                focusVisitUuid && v.visit_uuid === focusVisitUuid ? "ring-2 ring-slate-400" : ""
                              } hover:bg-slate-50 dark:hover:bg-slate-800/50`}
                              onClick={() => setExpandedIdx(expandedIdx === i ? null : i)}
                            >
                              <td className="px-2 py-2 text-center">{v.created_at ? format(new Date(v.created_at), "dd/MM/yyyy HH:mm") : "—"}</td>
                              <td className="px-2 py-2 text-center">{v.city     || "—"}</td>
                              <td className="px-2 py-2 text-center">{v.state    || "—"}</td>
                              <td className="px-2 py-2 text-center">{v.country  || "—"}</td>
                              <td className="px-2 py-2 text-center">{v.device_type || "—"}</td>
                              <td className="px-2 py-2 text-center">{v.reached_checkout ? "Sim" : "Não"}</td>
                              <td className="px-2 py-2 text-center">
                                {v.purchased ? <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">Sim</span> : "Não"}
                              </td>
                              <td className="px-2 py-2 text-center">{v.is_bot   ? "Sim" : "Não"}</td>
                              <td className="px-2 py-2 text-center">{v.is_vpn   ? "Sim" : "Não"}</td>
                              <td className="px-2 py-2 text-center">{v.is_proxy ? "Sim" : "Não"}</td>
                              <td className="px-2 py-2 text-center">{v.click_on_presell ? 1 : 0}</td>
                              <td className="px-2 py-2 text-center">
                                {expandedIdx === i ? <ChevronUp className="w-4 h-4 mx-auto" /> : <ChevronDown className="w-4 h-4 mx-auto" />}
                              </td>
                            </tr>

                            {expandedIdx === i && (
                              <tr>
                                <td colSpan={12} className="px-4 py-3 bg-slate-50 dark:bg-slate-800/40">
                                  <div className="grid md:grid-cols-2 gap-4">
                                    {/* Detalhes */}
                                    <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-3">
                                      <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-2 font-semibold">Detalhes da visita</div>
                                      {[
                                        { label: "ID da Visita (SVC)", value: v.visit_uuid, copy: v.visit_uuid, mono: true },
                                        { label: "GCLID",              value: v.gclid || "—", copy: v.gclid,   mono: true },
                                        ...(v.gbraid ? [{ label: "GBRAID", value: v.gbraid, copy: v.gbraid, mono: true }] : []),
                                        ...(v.wbraid ? [{ label: "WBRAID", value: v.wbraid, copy: v.wbraid, mono: true }] : []),
                                        { label: "Origem",             value: v.referrer || "—" },
                                        { label: "URL da visita",      value: v.page_url || "—" },
                                        { label: "Navegador",          value: v.user_agent || "—" },
                                      ].map(({ label, value, copy, mono }) => (
                                        <div key={label} className="flex items-start gap-1 mb-1 text-[11px]">
                                          <span className="font-semibold text-slate-700 dark:text-slate-300 shrink-0">{label}:</span>
                                          <div className="flex items-center gap-1 flex-1 min-w-0">
                                            <span className={`break-all text-slate-500 dark:text-slate-400 ${mono ? "font-mono" : ""}`}>{value}</span>
                                            {copy && copy !== "—" && (
                                              <button onClick={() => handleCopy(copy)} className="shrink-0 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"><Copy className="w-3 h-3" /></button>
                                            )}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                    {/* Parâmetros */}
                                    <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-3">
                                      <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-2 font-semibold">Parâmetros</div>
                                      <div className="flex flex-wrap gap-2">
                                        {ALL_PARAM_KEYS.map((key) => v[key] ? (
                                          <span key={key} className="bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded px-2 py-1 text-[10px] break-all">
                                            {translateField(key)}: {String(v[key])}
                                          </span>
                                        ) : null)}
                                      </div>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>

            <div className="flex justify-end gap-3 px-6 pb-5 border-t border-slate-200 dark:border-slate-700">
              <button className="inline-flex items-center px-4 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 text-sm" onClick={() => setSelectedIp(null)}>
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL BLOQUEAR IP ── */}
      {blockModalOpen && (
        <div className="fixed inset-0 z-[10000] bg-black/60 backdrop-blur-[1px] flex items-center justify-center px-4">
          <div className="w-full max-w-md rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 shadow-2xl">
            <div className="px-5 pt-5 pb-4 border-b border-slate-200 dark:border-slate-700">
              <h3 className="text-base font-bold">Bloquear IP</h3>
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">Escolha como deseja bloquear este IP.</p>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <div className="text-xs text-slate-500 mb-1">IP selecionado</div>
                <div className="font-mono text-sm break-all text-slate-900 dark:text-slate-100">{ipToBlock}</div>
              </div>
              <div className="space-y-2">
                {[
                  { value: "script", label: "Bloquear somente neste script" },
                  { value: "global", label: "Bloquear globalmente" },
                ].map(({ value, label }) => (
                  <label key={value} className="flex items-center gap-2 text-sm cursor-pointer select-none">
                    <input type="radio" name="blockScope" value={value} checked={blockScope === value} onChange={() => setBlockScope(value)} />
                    <span className="text-slate-700 dark:text-slate-300">{label}</span>
                  </label>
                ))}
              </div>
              <div>
                <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1">Observação</label>
                <textarea value={blockNote} onChange={(e) => setBlockNote(e.target.value)} rows={3} placeholder="Ex: IP suspeito visto no analytics"
                  className={`${inputCls} resize-none`} />
              </div>
            </div>
            <div className="flex justify-end gap-2 px-5 pb-5">
              <button onClick={() => setBlockModalOpen(false)} className="inline-flex items-center px-4 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 text-sm">
                Cancelar
              </button>
              <button onClick={handleConfirmBlockIp} disabled={blockingIp}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-red-200 dark:border-red-900 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 text-sm font-semibold disabled:opacity-60">
                {blockingIp ? "Bloqueando..." : "Confirmar bloqueio"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}